import { Component, computed, inject, input } from '@angular/core';

import { AggregatedEvaluationsSkillStatus } from '@core/evaluations-types';
import { I18nService } from '@core/i18n-legacy';
import type { IconName, IconToken } from '@core/user-interface-legacy';
import { BadgeComponent, IconComponent, RtTooltip } from '@core/user-interface-legacy';

interface EvaluationSkillBadgeIcon {
  name: IconName;
  color: IconToken;
}

@Component({
  selector: 'rt-evaluation-skill-badge',
  templateUrl: './evaluation-skill-badge.component.html',
  imports: [BadgeComponent, IconComponent, RtTooltip],
})
export class EvaluationSkillBadgeComponent {
  private readonly i18n = inject(I18nService);

  private readonly icons: {
    status: Record<AggregatedEvaluationsSkillStatus, EvaluationSkillBadgeIcon>;
    mixedTeamFeedback: EvaluationSkillBadgeIcon;
  } = {
    status: {
      [AggregatedEvaluationsSkillStatus.AboveRequiredLevel]: {
        name: 'trend-up',
        color: 'colorIconSuccessDefault',
      },
      [AggregatedEvaluationsSkillStatus.AtRequiredLevel]: {
        name: 'check-small',
        color: 'colorIconSuccessDefault',
      },
      [AggregatedEvaluationsSkillStatus.BelowRequiredLevel]: {
        name: 'trend-down',
        color: 'colorIconCriticalDefault',
      },
      [AggregatedEvaluationsSkillStatus.NoEvaluated]: {
        name: 'remove',
        color: 'colorIconLowDefault',
      },
      [AggregatedEvaluationsSkillStatus.NoRequiredLevel]: {
        name: 'help-plain',
        color: 'colorIconPrimaryDefault',
      },
    },
    mixedTeamFeedback: {
      name: 'warning',
      color: 'colorIconWarningDefault',
    },
  };

  protected readonly translation = {
    status: {
      [AggregatedEvaluationsSkillStatus.AboveRequiredLevel]: this.i18n.translate(
        'app.interview.summary.skills_summary.status.above_required_level',
      ),
      [AggregatedEvaluationsSkillStatus.AtRequiredLevel]: this.i18n.translate(
        'app.interview.summary.skills_summary.status.at_required_level_full',
      ),
      [AggregatedEvaluationsSkillStatus.BelowRequiredLevel]: this.i18n.translate(
        'app.interview.summary.skills_summary.status.below_required_level',
      ),
      [AggregatedEvaluationsSkillStatus.NoEvaluated]: this.i18n.translate(
        'app.interview.summary.skills_summary.status.not_evaluated',
      ),
      [AggregatedEvaluationsSkillStatus.NoRequiredLevel]: this.i18n.translate(
        'app.interview.summary.skills_summary.status.not_required_level',
      ),
    },
    mixedTeamFeedback: this.i18n.translate(
      'app.interview.summary.skills_summary.mixed_team_feedback',
    ),
    noRequiredLevelTooltip: this.i18n.translate(
      'app.interview.summary.skills_summary.no_required_level_tooltip',
    ),
  };

  protected readonly badgeInfo = computed(() => {
    const status = this.status();

    if (status) {
      return {
        label: this.translation.status[status],
        tooltip:
          status === AggregatedEvaluationsSkillStatus.NoRequiredLevel
            ? this.translation.noRequiredLevelTooltip
            : '',
        icon: this.icons.status[status],
      };
    }

    if (this.mixedTeamFeedback()) {
      return {
        label: this.translation.mixedTeamFeedback,
        tooltip: '',
        icon: this.icons.mixedTeamFeedback,
      };
    }

    return null;
  });

  public status = input<AggregatedEvaluationsSkillStatus>();

  public mixedTeamFeedback = input(false);
}
